import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';
import { lock, unlock } from '../utils/scrollLock';

/**
 * ImageCropModal - Square crop interface for profile photos
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - imageSrc: string - Base64 or URL of the image to crop
 * - onCropComplete: (croppedBlob: Blob, croppedDataUrl: string) => void
 * - aspectRatio: number - Default 1 (square)
 */
export default function ImageCropModal({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1
}) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  
  const CROP_SIZE = 280; // Size of the crop area in pixels

  // Manage scroll lock - only lock if not on mobile to allow scrolling to controls
  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    if (isOpen && !isMobile) {
      lock();
    }
    return () => {
      if (isOpen && !isMobile) {
        unlock();
      }
    };
  }, [isOpen]);

  // Reset state when image changes
  useEffect(() => {
    if (imageSrc) {
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    }
  }, [imageSrc]);

  // Load image and calculate initial size
  useEffect(() => {
    if (!imageSrc) return;
    
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Limit movement to keep image within bounds
    const maxMove = (zoom - 1) * CROP_SIZE / 2 + CROP_SIZE * 0.5;
    
    setPosition({
      x: Math.max(-maxMove, Math.min(maxMove, newX)),
      y: Math.max(-maxMove, Math.min(maxMove, newY))
    });
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    const maxMove = (zoom - 1) * CROP_SIZE / 2 + CROP_SIZE * 0.5;
    
    setPosition({
      x: Math.max(-maxMove, Math.min(maxMove, newX)),
      y: Math.max(-maxMove, Math.min(maxMove, newY))
    });
  }, [isDragging, dragStart, zoom]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleZoomChange = (value) => {
    const newZoom = value[0];
    setZoom(newZoom);
    
    // Adjust position if needed to keep image in bounds
    const maxMove = (newZoom - 1) * CROP_SIZE / 2 + CROP_SIZE * 0.5;
    setPosition(prev => ({
      x: Math.max(-maxMove, Math.min(maxMove, prev.x)),
      y: Math.max(-maxMove, Math.min(maxMove, prev.y))
    }));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleCrop = async () => {
    if (!imageSrc) return;

    // Create canvas for cropping
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Output size (512x512 for high quality)
    const outputSize = 512;
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Load the image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageSrc;
    });

    // Calculate the crop region based on zoom and position
    const scale = Math.min(img.width, img.height) / CROP_SIZE;
    const cropSize = CROP_SIZE * scale / zoom;
    
    // Center of the image
    const centerX = img.width / 2;
    const centerY = img.height / 2;
    
    // Offset from position (scaled)
    const offsetX = -position.x * scale / zoom;
    const offsetY = -position.y * scale / zoom;
    
    // Source coordinates
    const sx = centerX - cropSize / 2 + offsetX;
    const sy = centerY - cropSize / 2 + offsetY;
    
    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(outputSize / 2, outputSize / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-outputSize / 2, -outputSize / 2);
    }

    // Draw the cropped image
    ctx.drawImage(
      img,
      Math.max(0, sx),
      Math.max(0, sy),
      Math.min(cropSize, img.width - sx),
      Math.min(cropSize, img.height - sy),
      0,
      0,
      outputSize,
      outputSize
    );

    // Convert to blob
    canvas.toBlob((blob) => {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      onCropComplete(blob, dataUrl);
    }, 'image/jpeg', 0.9);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-background/80 backdrop-blur-sm overflow-y-auto py-4 sm:py-0 overscroll-contain">
      <div 
        className="bg-card border border-border rounded-xl w-full max-w-md mx-4 my-auto overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 2rem)', maxHeight: 'calc(100dvh - 2rem)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="font-heading font-bold text-lg">Crop Photo</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Crop Area - flex-shrink to allow scrolling */}
        <div 
          ref={containerRef}
          className="relative w-full aspect-square bg-black overflow-hidden cursor-move select-none flex-shrink-0 touch-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Image */}
          {imageSrc && (
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Crop preview"
                className="max-w-none"
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: `${CROP_SIZE * 2}px`,
                  maxHeight: `${CROP_SIZE * 2}px`,
                  minWidth: `${CROP_SIZE}px`,
                  minHeight: `${CROP_SIZE}px`,
                  objectFit: 'contain'
                }}
                draggable={false}
              />
            </div>
          )}

          {/* Crop overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Dark corners */}
            <div className="absolute inset-0 bg-background/60" 
              style={{
                clipPath: `polygon(
                  0 0, 100% 0, 100% 100%, 0 100%,
                  0 50%,
                  calc(50% - ${CROP_SIZE/2}px) calc(50% - ${CROP_SIZE/2}px),
                  calc(50% - ${CROP_SIZE/2}px) calc(50% + ${CROP_SIZE/2}px),
                  calc(50% + ${CROP_SIZE/2}px) calc(50% + ${CROP_SIZE/2}px),
                  calc(50% + ${CROP_SIZE/2}px) calc(50% - ${CROP_SIZE/2}px),
                  calc(50% - ${CROP_SIZE/2}px) calc(50% - ${CROP_SIZE/2}px),
                  0 50%
                )`
              }}
            />
            
            {/* Circular crop guide */}
            <div 
              className="absolute rounded-full border-2 border-white shadow-lg"
              style={{
                width: CROP_SIZE,
                height: CROP_SIZE,
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          </div>

          {/* Instructions */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/70 px-3 py-1.5 rounded-full">
            <p className="text-xs text-foreground/80">Drag to reposition</p>
          </div>
        </div>

        {/* Controls - flex-shrink-0 to ensure visibility */}
        <div className="p-4 space-y-4 border-t border-border flex-shrink-0">
          {/* Zoom Control */}
          <div className="flex items-center gap-4">
            <ZoomOut className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <Slider
              value={[zoom]}
              onValueChange={handleZoomChange}
              min={1}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </div>

          {/* Action Buttons - stacked on mobile */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRotate}
                className="flex-shrink-0"
              >
                <RotateCw className="w-4 h-4 mr-1" />
                Rotate
              </Button>
              <div className="flex-1 hidden sm:block" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-initial">
                Cancel
              </Button>
              <Button 
                onClick={handleCrop}
                className="bg-secondary hover:bg-secondary/90 flex-1 sm:flex-initial"
                data-testid="crop-apply-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                Apply
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
